import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Wheat, Leaf, Clock } from 'lucide-react';
import { products } from '../data/mock';
import { useLanguage } from '../context/LanguageContext';

const HomePage = () => {
  const navigate = useNavigate();
  const { t, currentLang } = useLanguage();
  const isRTL = currentLang === 'ar' || currentLang === 'ku';
  
  // Slideshow images
  const heroImages = [
    "https://images.pexels.com/photos/25810607/pexels-photo-25810607.jpeg",
    "https://images.pexels.com/photos/5323489/pexels-photo-5323489.jpeg",
    ...products.map(p => p.image).slice(0, 3)
  ];
  
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % heroImages.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [heroImages.length]);

  const features = [
    { 
      icon: <Clock className="w-8 h-8" />, 
      title: t.features?.fresh?.title || 'طازج يومياً', 
      desc: t.features?.fresh?.desc || 'نخبز كل صباح قبل الفجر' 
    },
    { 
      icon: <Leaf className="w-8 h-8" />, 
      title: t.features?.natural?.title || 'مكونات طبيعية', 
      desc: t.features?.natural?.desc || 'بدون مواد حافظة أو مضافة' 
    },
    { 
      icon: <Wheat className="w-8 h-8" />, 
      title: t.features?.traditional?.title || 'وصفات أصيلة', 
      desc: t.features?.traditional?.desc || 'موروثة عبر الأجيال' 
    }
  ];

  return (
    <div className="min-h-screen bg-[#faf8f5]">
      {/* Hero Section - Cinematic Full Screen */}
      <section className="relative h-screen flex items-center overflow-hidden">
        {/* Background Slideshow */}
        <div className="absolute inset-0">
          {heroImages.map((image, index) => (
            <div
              key={index}
              className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
                index === currentImageIndex ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <img 
                src={image}
                alt={`Dura Alsham ${index + 1}`}
                className="w-full h-full object-cover scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0d4023]/95 via-[#0d4023]/50 to-[#0d4023]/30" />
            </div>
          ))}
        </div>

        {/* Hero Content - Asymmetric Layout */}
        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-12">
          <div className={`max-w-2xl ${isRTL ? 'ms-auto text-end' : 'me-auto text-start'}`}>
            {/* Overline Badge */}
            <div 
              className={`inline-flex items-center gap-2 mb-8 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
              style={{ transitionDelay: '200ms' }}
            >
              <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#b89759]">
                {t.hero?.badge || 'طازج يومياً منذ 2020'}
              </span>
              <div className="w-12 h-[1px] bg-[#b89759]" />
            </div>
            
            {/* Main Heading */}
            <h1 
              className={`font-heading text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-[1.1] tracking-tight transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ 
                transitionDelay: '400ms',
                textShadow: '0 2px 20px rgba(0,0,0,0.5)'
              }}
            >
              {t.hero?.title || 'أفران درة الشام'}
              <br />
              <span className="text-[#b89759]" style={{ textShadow: '0 2px 20px rgba(0,0,0,0.4)' }}>
                {t.hero?.subtitle || 'الخبز الأصيل'}
              </span>
            </h1>
            
            {/* Description */}
            <p 
              className={`text-lg md:text-xl text-white/80 mb-10 max-w-xl leading-relaxed transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ transitionDelay: '600ms' }}
            >
              {t.hero?.description || 'تقاليد عريقة من بلاد الشام، جودة ألمانية، وطعم لا يُنسى'}
            </p>
            
            {/* CTA Buttons */}
            <div 
              className={`flex flex-wrap gap-4 ${isRTL ? 'justify-end' : 'justify-start'} transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ transitionDelay: '800ms' }}
            >
              <button
                onClick={() => navigate('/products')}
                className="group inline-flex items-center gap-3 px-8 py-4 bg-[#b89759] text-[#0d4023] rounded-full text-base font-semibold hover:bg-[#cba660] transition-colors duration-300"
                data-testid="discover-products-btn"
              >
                <span>{t.hero?.cta || 'اكتشف منتجاتنا'}</span>
                <ArrowRight className={`w-5 h-5 transition-transform duration-300 ${isRTL ? 'group-hover:-translate-x-1 rotate-180' : 'group-hover:translate-x-1'}`} />
              </button>
              
              <button
                onClick={() => navigate('/contact')}
                className="inline-flex items-center gap-3 px-8 py-4 border-2 border-white/30 text-white rounded-full text-base font-semibold hover:bg-white/10 transition-colors duration-300"
                data-testid="contact-btn"
              >
                {t.hero?.contactUs || 'تواصل معنا'}
              </button>
            </div>
          </div>
        </div>

        {/* Slideshow Indicators */}
        <div className={`absolute bottom-12 ${isRTL ? 'start-12' : 'end-12'} flex gap-2`}>
          {heroImages.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentImageIndex(index)}
              data-testid={`slide-indicator-${index}`}
              className={`transition-all duration-300 rounded-full ${
                index === currentImageIndex 
                  ? 'w-10 h-2 bg-[#b89759]' 
                  : 'w-2 h-2 bg-white/40 hover:bg-white/60'
              }`}
            />
          ))}
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-12 start-1/2 transform -translate-x-1/2 hidden md:block">
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center">
            <div className="w-1 h-3 bg-[#b89759] rounded-full mt-2 animate-bounce" />
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 lg:py-32 bg-[#faf8f5]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          {/* Section Header */}
          <div className={`text-center mb-16`}>
            <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#b89759] block mb-4">
              {currentLang === 'ar' ? 'لماذا نحن' : currentLang === 'de' ? 'Warum wir' : 'Why Us'}
            </span>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-[#0d4023] tracking-tight">
              {currentLang === 'ar' ? 'تميّز في كل رغيف' : currentLang === 'de' ? 'Exzellenz in jedem Laib' : 'Excellence in Every Loaf'}
            </h2>
          </div>

          {/* Features Grid */}
          <div className="grid md:grid-cols-3 gap-8 lg:gap-12">
            {features.map((feature, i) => (
              <div 
                key={i} 
                className={`group p-8 lg:p-10 border border-[#b89759]/20 rounded-2xl hover:border-[#b89759]/50 transition-colors duration-300 bg-white/50 ${isRTL ? 'text-end' : 'text-start'}`}
                data-testid={`feature-card-${i}`}
              >
                <div className="w-16 h-16 rounded-full bg-[#0d4023]/5 flex items-center justify-center text-[#0d4023] mb-6 group-hover:bg-[#0d4023] group-hover:text-[#b89759] transition-colors duration-300">
                  {feature.icon}
                </div>
                <h3 className="font-heading text-2xl font-bold mb-3 text-[#0d4023]">
                  {feature.title}
                </h3>
                <p className="text-[#5a7062] leading-relaxed">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products Preview */}
      <section className="py-24 lg:py-32 bg-[#0d4023]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          {/* Section Header */}
          <div className={`flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16`}>
            <div className={isRTL ? 'text-end' : 'text-start'}>
              <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#b89759] block mb-4">
                {t.products?.title || 'منتجاتنا'}
              </span>
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white tracking-tight">
                {currentLang === 'ar' ? 'من أفراننا إليكم' : currentLang === 'de' ? 'Frisch aus unserem Ofen' : 'Fresh from Our Oven'}
              </h2>
            </div>
            <button
              onClick={() => navigate('/products')}
              className={`group inline-flex items-center gap-2 text-[#b89759] font-medium hover:text-white transition-colors duration-300 ${isRTL ? 'flex-row-reverse' : ''}`}
              data-testid="view-all-products-btn"
            >
              <span>{currentLang === 'ar' ? 'عرض الكل' : currentLang === 'de' ? 'Alle anzeigen' : 'View All'}</span>
              <ArrowRight className={`w-5 h-5 transition-transform duration-300 ${isRTL ? 'group-hover:-translate-x-1 rotate-180' : 'group-hover:translate-x-1'}`} />
            </button>
          </div>

          {/* Products Grid - Asymmetric */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 lg:gap-6">
            {products.slice(0, 4).map((product, i) => (
              <div 
                key={product.id}
                className={`group relative overflow-hidden rounded-2xl ${i === 0 ? 'col-span-2 row-span-2' : ''}`}
                data-testid={`product-preview-${product.id}`}
              >
                <div className={`aspect-square ${i === 0 ? 'md:aspect-auto md:h-full' : ''}`}>
                  <img 
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-[#0d4023]/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className={`absolute bottom-0 start-0 end-0 p-4 lg:p-6 translate-y-4 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-300 ${isRTL ? 'text-end' : 'text-start'}`}>
                  <h3 className="font-heading text-lg lg:text-xl font-bold text-white">{product.name}</h3>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 lg:py-32 bg-[#faf8f5]">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#b89759] block mb-4">
            {currentLang === 'ar' ? 'عملاء الجملة' : currentLang === 'de' ? 'Geschäftskunden' : 'Business Customers'}
          </span>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-[#0d4023] tracking-tight mb-6">
            {currentLang === 'ar' ? 'هل أنت صاحب مطعم أو سوبرماركت؟' : currentLang === 'de' ? 'Restaurant- oder Supermarktbesitzer?' : 'Restaurant or Supermarket Owner?'}
          </h2>
          <p className="text-lg text-[#5a7062] mb-10 max-w-2xl mx-auto">
            {currentLang === 'ar' 
              ? 'انضم إلى شركائنا التجاريين واحصل على أسعار خاصة وتوصيل منتظم'
              : currentLang === 'de'
              ? 'Werden Sie unser Geschäftspartner und erhalten Sie Sonderpreise und regelmäßige Lieferungen'
              : 'Join our business partners and get special prices and regular deliveries'}
          </p>
          <button
            onClick={() => navigate('/login')}
            className="inline-flex items-center gap-3 px-10 py-4 bg-[#0d4023] text-[#faf8f5] rounded-full text-base font-semibold hover:bg-[#1a5c32] transition-colors duration-300"
            data-testid="b2b-cta-btn"
          >
            {currentLang === 'ar' ? 'سجّل كعميل تجاري' : currentLang === 'de' ? 'Als Geschäftskunde registrieren' : 'Register as Business Customer'}
          </button>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
